// ─── AUTH CONTEXT ─────────────────────────────────────────────────────────────
// Holds Supabase auth state and the backend-derived tier/profile. Stores the
// token + tier in localStorage so backendFetch and AnalystButton can read them.

import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { clearAcknowledged } from "./acknowledgement";
import { backendFetch } from "./core";
import { getSession, signOut as sbSignOut, supabase } from "./supabase";

const AuthContext = createContext({
  user: null, session: null, loading: true, tier: null, me: null,
  signOut: async () => {}, refreshMe: async () => null,
});

export function useAuth() {
  return useContext(AuthContext);
}

// Fetch the account profile from the backend and cache the useful bits.
async function loadProfile() {
  try {
    const res = await backendFetch("https://api", "/auth/me");
    if (!res.ok) return null;
    const me = await res.json();
    localStorage.setItem("qs_tier", me.tier || "");
    if (me.trial_end) localStorage.setItem("qs_trial_end", me.trial_end);
    else localStorage.removeItem("qs_trial_end");
    if (typeof me.ai_calls_limit === "number") localStorage.setItem("qs_ai_limit", String(me.ai_calls_limit));
    if (typeof me.ai_calls_used_today === "number") localStorage.setItem("qs_ai_used", String(me.ai_calls_used_today));
    return me;
  } catch {
    return null;
  }
}

// ⚠ qs_auth_token BELONGS ENTIRELY TO THIS FILE'S SUPABASE FLOW. The internal
// session lives under its own key (core.jsx: INTERNAL_TOKEN_KEY) and is never
// read or written here, so clearStored() below cannot reach it.
function clearStored() {
  localStorage.removeItem("qs_auth_token");
  localStorage.removeItem("qs_tier");
  localStorage.removeItem("qs_trial_end");
  localStorage.removeItem("qs_ai_limit");
  localStorage.removeItem("qs_ai_used");
  clearAcknowledged();
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tier,    setTier]    = useState(null);
  const [me,      setMe]      = useState(null);

  const applySession = useCallback(async (sess) => {
    setSession(sess);
    setUser(sess?.user ?? null);
    if (sess?.access_token) {
      localStorage.setItem("qs_auth_token", sess.access_token);
      const profile = await loadProfile();
      setMe(profile);
      setTier(profile?.tier ?? null);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    const profile = await loadProfile();
    setMe(profile);
    setTier(profile?.tier ?? null);
    return profile;
  }, []);

  const doSignOut = useCallback(async () => {
    await sbSignOut();
    clearStored();
    setSession(null);
    setUser(null);
    setTier(null);
    setMe(null);
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const { data } = await getSession();
      if (!mounted) return;
      if (data?.session) await applySession(data.session);
      if (mounted) setLoading(false);
    })();

    let subscription;
    if (supabase) {
      const { data } = supabase.auth.onAuthStateChange(async (event, sess) => {
        if (event === "SIGNED_OUT" || !sess) {
          // ⚠ Supabase emits INITIAL_SESSION with session: null whenever nobody
          // is signed in — the internal user's NORMAL state. That used to matter
          // enormously, because this clearStored() shared a key with the internal
          // session and destroyed it on every load; cef4682 guarded the call.
          // The guard is GONE because the collision is gone: the internal token
          // now has its own key and clearStored() cannot reach it. Guarding a
          // write site is a workaround; not sharing the slot is the fix.
          clearStored();
          setSession(null); setUser(null); setTier(null); setMe(null);
        } else if (event === "TOKEN_REFRESHED") {
          if (sess.access_token) localStorage.setItem("qs_auth_token", sess.access_token);
          setSession(sess);
        } else {
          // SIGNED_IN · INITIAL_SESSION · USER_UPDATED
          await applySession(sess);
        }
      });
      subscription = data?.subscription;
    }

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
    };
  }, [applySession]);

  return (
    <AuthContext.Provider value={{ user, session, loading, tier, me, signOut: doSignOut, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}
